/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/naming-convention */
import type { Component, JSX } from 'solid-js';
import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  Show,
  untrack,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import WithLabel from '../WithLabel/WithLabel';
import CustomError from '../CustomError/CustomError';
// Use Solid components internally for reliable prop flow

export interface CustomSelectProps {
  name: string;
  label: string;
  options: readonly unknown[] | string;
  getValue?(item: unknown): string | number;
  getDisplayValue?(item: unknown): string;
  defaultValue?: string | number;
  value?: string | number;
  onChange?(
    event: Event & { currentTarget: HTMLSelectElement; target: Element }
  ): void;
  errorMsg?: string;
  disabled?: boolean;
  id?: string;
  'data-cy'?: string;
  dataCy?: string;
  /** Selects the rendering strategy. Defaults to a native `<select>`. */
  renderMode?: 'native' | 'menu';
  /** Menu mode only: shows an indeterminate selection state (e.g. "mixed", "multiple values"). */
  indeterminate?: boolean;
  /** Menu mode only: label shown as a disabled row when `indeterminate` is true. */
  indeterminateLabel?: string;
  /** Menu mode only: skips the `WithLabel` wrapper for compact/unlabeled usage. */
  hideLabel?: boolean;
  /** Menu mode only: extra classes appended to the trigger container. */
  triggerClassName?: string;
  /** Menu mode only: extra classes appended to the inner clickable link element. */
  linkClassName?: string;
  /** Menu mode only: extra classes appended to the trigger's arrow icon. */
  arrowIconClassName?: string;
  /** Menu mode only: extra classes appended to the menu container. */
  menuClassName?: string;
  /** Menu mode only: renders the menu through a `Portal` with viewport-aware fixed positioning. */
  usePortal?: boolean;
  /** Menu mode only: fired with the selected option's value when an item is picked. */
  onSelect?(value: string | number): void;
}

// Approximate menu height used to flip the menu above the trigger when the
// trigger is near the viewport bottom. Two visible rows + padding ≈ 5rem.
const APPROX_MENU_HEIGHT_PX = 80;
const VIEWPORT_PADDING_PX = 8;

const CustomSelect: Component<CustomSelectProps> = (props) => {
  const v = untrack(() => props.value) ?? untrack(() => props.defaultValue);

  const [innerValue, setInnerValue] = createSignal<string | number | undefined>(
    v
  );

  createEffect(() => {
    if (props.value !== undefined) {
      setInnerValue(props.value);
    }
  });

  const getDataCy = () => props.dataCy ?? props['data-cy'] ?? props.name;
  const hasError = () => Boolean(props.errorMsg);
  const selectClass = () => (hasError() ? 'error' : '');
  const parsedOptions = () => {
    const opts = props.options;
    if (Array.isArray(opts)) return opts as readonly unknown[];
    if (typeof opts === 'string') {
      try {
        const parsed = JSON.parse(opts);
        return Array.isArray(parsed)
          ? (parsed as unknown[])
          : ([] as unknown[]);
      } catch {
        return [] as unknown[];
      }
    }
    return [] as unknown[];
  };
  const valueFn = (item: unknown) =>
    props.getValue ? props.getValue(item) : (item as string | number);
  const displayFn = (item: unknown) =>
    props.getDisplayValue ? props.getDisplayValue(item) : String(item ?? '');

  const emit = (
    eventName: string,
    event: Event,
    detail?: Record<string, unknown>
  ): void => {
    const customEvent = new CustomEvent(eventName, {
      detail: { ...(detail ?? {}), originalEvent: event },
      bubbles: true,
    });
    (event.target as HTMLElement | null)?.dispatchEvent(customEvent);
  };

  const handleChange: JSX.EventHandlerUnion<HTMLSelectElement, Event> = (e) => {
    const target = e.currentTarget as HTMLSelectElement;
    const newValue: string = target.value;
    setInnerValue(newValue);
    emit('tc-customselect-change', e as Event, { value: newValue });
    props.onChange?.(
      e as Event & { currentTarget: HTMLSelectElement; target: Element }
    );
  };

  const currentValue = () =>
    props.value !== undefined ? props.value : innerValue();

  const currentDisplayValue = () => {
    const match = parsedOptions().find(
      (item) => valueFn(item) === currentValue()
    );
    return match !== undefined ? displayFn(match) : '';
  };

  const [isOpen, setIsOpen] = createSignal(false);
  const [menuStyle, setMenuStyle] = createSignal<JSX.CSSProperties>({});
  let triggerRef: HTMLDivElement | undefined;
  let menuRef: HTMLDivElement | undefined;

  const label = () =>
    props.indeterminate
      ? (props.indeterminateLabel ?? '')
      : currentDisplayValue();

  const updatePosition = (): void => {
    if (!triggerRef) return;
    const rect = triggerRef.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpwards =
      spaceBelow < APPROX_MENU_HEIGHT_PX + VIEWPORT_PADDING_PX;

    const top = openUpwards
      ? rect.top + window.scrollY - APPROX_MENU_HEIGHT_PX
      : rect.bottom + window.scrollY;

    setMenuStyle({
      position: 'absolute',
      top: `${top}px`,
      left: `${rect.left + window.scrollX}px`,
      'z-index': '9999',
    });
  };

  const openMenu = (): void => {
    if (props.usePortal) {
      updatePosition();
    }
    setIsOpen(true);
  };

  const handleToggle = (event: MouseEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    if (props.disabled) return;
    if (isOpen()) {
      setIsOpen(false);
    } else {
      openMenu();
    }
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (props.disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isOpen()) {
        setIsOpen(false);
      } else {
        openMenu();
      }
    } else if (event.key === 'Escape' && isOpen()) {
      setIsOpen(false);
    }
  };

  const handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as Node | null;
    if (!target) return;
    const insideTrigger = triggerRef?.contains(target) ?? false;
    const insideMenu = menuRef?.contains(target) ?? false;
    if (!insideTrigger && !insideMenu) {
      setIsOpen(false);
    }
  };

  const handleScroll = (event: Event): void => {
    const target = event.target as Node | null;
    if (target && menuRef?.contains(target)) return;
    setIsOpen(false);
  };

  const handleResize = (): void => {
    if (isOpen() && props.usePortal) {
      updatePosition();
    }
  };

  const handleMenuSelect = (item: unknown, event: MouseEvent): void => {
    event.stopPropagation();
    const newValue = valueFn(item);
    setInnerValue(newValue);
    setIsOpen(false);
    props.onSelect?.(newValue);
  };

  // Attach global listeners only while the menu is open, to avoid O(n) global
  // listeners per rendered row when many menu-mode selects coexist on a page.
  createEffect(() => {
    if (props.renderMode !== 'menu') return;
    if (!isOpen()) return;
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    onCleanup(() => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    });
  });

  const getClassNames = (...classes: (string | undefined | boolean)[]) => {
    return classes.filter(Boolean).join(' ');
  };

  const menuContent = () => (
    <div
      ref={menuRef}
      class={getClassNames('dropdown-pane is-open w-auto', props.menuClassName)}
      style={menuStyle()}
    >
      <ul class="dropdown-list">
        <Show when={props.indeterminate}>
          <li class="list-item disabled not-allowed">
            {props.indeterminateLabel}
          </li>
        </Show>
        <For each={parsedOptions()}>
          {(item) => {
            const isActive = () =>
              !props.indeterminate && valueFn(item) === currentValue();
            return (
              <li
                class={getClassNames('list-item', isActive() && 'active')}
                onClick={(event) => {
                  handleMenuSelect(item, event);
                }}
              >
                <span class="block text">{displayFn(item)}</span>
                <Show when={isActive()}>
                  <i class="icon-font tc-icon-check" />
                </Show>
              </li>
            );
          }}
        </For>
      </ul>
    </div>
  );

  const menuVariant = () => (
    <div
      ref={triggerRef}
      class={getClassNames(
        'dropdown-pane-container',
        props.triggerClassName,
        props.disabled && 'disabled'
      )}
      onClick={handleToggle}
      data-cy={getDataCy()}
    >
      <div
        class={getClassNames(
          'name dropdownpane-link text-ellipsis',
          props.linkClassName,
          props.disabled && 'disabled'
        )}
        role="button"
        tabindex={props.disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen()}
        onKeyDown={handleKeyDown}
      >
        <span class="text-ellipsis">{label()}</span>
        <i
          class={getClassNames(
            'icon-font',
            isOpen() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down',
            props.arrowIconClassName
          )}
        />
      </div>
      {hasError() && <CustomError msg={props.errorMsg!} />}
      <Show when={isOpen()}>
        <Show when={props.usePortal} fallback={menuContent()}>
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          <Portal>{menuContent()}</Portal>
        </Show>
      </Show>
    </div>
  );

  const nativeVariant = () => (
    <WithLabel name={props.name} label={props.label} id={props.id}>
      <div
        class="input-focus-group custom-select-wrapper"
        style={{ position: 'relative' }}
      >
        <select
          id={props.name}
          class={`${selectClass()} pl-2 custom-select-native`}
          data-cy={getDataCy()}
          value={currentValue()}
          onChange={handleChange}
          disabled={props.disabled}
          style={{
            appearance: 'none',
            '-webkit-appearance': 'none',
            '-moz-appearance': 'none',
            'background-image': 'none',
            'padding-right': '1.75rem',
          }}
        >
          {props.defaultValue !== undefined && (
            <option value={props.defaultValue} disabled>
              {String(props.defaultValue)}
            </option>
          )}
          <For each={parsedOptions()}>
            {(option) => (
              <option value={valueFn(option)}>{displayFn(option)}</option>
            )}
          </For>
        </select>
        <i
          aria-hidden="true"
          class="icon-font tc-icon-arrow-drop-down custom-select-arrow"
          style={{
            position: 'absolute',
            right: '0.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            'pointer-events': 'none',
          }}
        />
        <div class="line" />
      </div>
      {hasError() && <CustomError msg={props.errorMsg!} />}
    </WithLabel>
  );

  return (
    <Show when={props.renderMode === 'menu'} fallback={nativeVariant()}>
      <Show
        when={props.hideLabel}
        fallback={
          <WithLabel name={props.name} label={props.label} id={props.id}>
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {menuVariant()}
          </WithLabel>
        }
      >
        {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
        {menuVariant()}
      </Show>
    </Show>
  );
};

export default CustomSelect;
